// src/components/Home/TourismGrowthSection.jsx
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { tourismGrowthData } from "../../config/homePageUtils";
import Button from "../common/Button";
import { useLanguage } from "../LanguageProvider/LanguageProvider";

ChartJS.register(ArcElement, Tooltip, Legend);

// ✅ Custom plugin for center text
const centerTextPlugin = {
  id: "centerText",
  beforeDraw: (chart, args, options) => {
    const { width, height, ctx } = chart;
    ctx.save();

    const dataset = chart.data.datasets[0].data;
    const total = dataset.reduce((a, b) => a + b, 0);

    const text1 = `${total}k`;
    const text2 = options.language ? "कुल आगमन" : "Total Arrivals";

    ctx.font = "bold 16px Arial";
    ctx.fillStyle = "#333";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text1, width / 2, height / 2 - 10);

    ctx.font = "12px Arial";
    ctx.fillStyle = "#666";
    ctx.fillText(text2, width / 2, height / 2 + 10);

    ctx.restore();
  },
};

ChartJS.register(centerTextPlugin);

const TourismGrowthSection = () => {
  const { language } = useLanguage();
  const {
    heading,
    highlightColor,
    description,
    button,
    charts,
    backgroundImg,
  } = tourismGrowthData;

  /* ✅ Inject language-safe labels into Chart.js */
  const getChartData = (chart) => ({
    ...chart.data,
    labels: chart.data.labels.map((l) => (language ? l.hi : l.en)),
    datasets: chart.data.datasets.map((ds) => ({
      ...ds,
      label: language ? ds.label.hi : ds.label.en,
    })),
  });

  const commonOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "65%",
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        callbacks: {
          label: function (context) {
            let value = context.raw;

            value = value + "k";

            return `${context.label}: ${value}`;
          },
        },
      },
      centerText: {
        language,
      },
    },
  };

  const chartColors = [
    "bg-chartBlue",
    "bg-chartGreen",
    "bg-chartYellow",
    "bg-chartOrange",
  ];

  return (
    <section
      id="tourism-growth"
      className="w-full bg-[#fceddb] py-16 relative overflow-hidden"
    >
      <div className="container mx-auto px-6 flex flex-col lg:flex-row items-center gap-12">
        {/* Text Content */}
        <div className="max-w-xl relative z-10">
          <h2 className="text-3xl sm:text-3xl font-extrabold mb-4 leading-tight">
            {language ? heading.hi : heading.en}
          </h2>
          <div className="h-1 w-24 mb-6 bg-highlight" />

          <p className="text-gray-700 mb-8 leading-relaxed">
            {language ? description.hi : description.en}
          </p>

          <Button
            onClick={() =>
              window.open(button.link, "_blank", "noopener,noreferrer")
            }
            className={`inline-flex items-center ${button.bgColor} text-white px-6 py-3 rounded-full font-400 hover:bg-[#000] transition`}
          >
            {language ? button.text.hi : button.text.en}
            <span className="ml-2 text-lg text-white">→</span>
          </Button>
        </div>

        {/* Chart Cards */}
        <div className="flex flex-col sm:flex-row gap-6 relative z-10">
          {charts.map((chart, idx) => (
            <div key={idx} className="bg-white shadow p-4 w-80 h-80">
              <h3 className="text-sm font-semibold mb-2">
                {language ? chart.title.hi : chart.title.en}
              </h3>

              <div className="h-56">
                <Doughnut data={getChartData(chart)} options={commonOptions} />
              </div>
              <div className="flex justify-between text-xs mt-4">
                {chart.data.labels.map((year, i) => (
                  <span key={i} className="flex items-center">
                    <span
                      className={`w-3 h-3 inline-block mr-1 rounded-full ${chartColors[i]}`}
                    />
                    {language ? year.hi : year.en}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Background Image on Right */}
      <div className="absolute top-0 right-0 w-1/3 h-full hidden lg:block z-[1]">
        <img
          src={backgroundImg}
          alt="Tourism Mountains"
          className="w-full h-full object-cover"
        />
      </div>
    </section>
  );
};

export default TourismGrowthSection;
